import { request } from '@/api/request'

/** Users, roles, the permission catalog and teams (users.manage / roles.manage / teams.manage, z8nrz7fxtz). */

export type UserKind = 'AGENT' | 'CUSTOMER'
/** 'not_configured' = no Cognito pool here (local dev login): the user signs in with the dev login. */
export type InviteResult = 'sent' | 'exists' | 'not_configured'

export interface AdminUser {
  id: string
  email: string
  firstName: string
  lastName: string
  kind: UserKind
  title: string | null
  tier: number | null
  isSuperAdmin: boolean
  enabled: boolean
  /** Has signed in at least once (bound to Cognito). */
  signedIn: boolean
  /** An invite is outstanding: a sign-in service is configured, the user is enabled and never signed in. */
  invitePending: boolean
  createdAt: string
  updatedAt: string
  role: { id: string; slug: string; displayName: string; kind: UserKind } | null
  team: { id: string; name: string } | null
  customer: { id: string; name: string } | null
  tenant: { id: string; name: string } | null
}

export interface UserInput {
  email?: string
  firstName?: string
  lastName?: string
  kind?: UserKind
  roleId?: string
  title?: string | null
  teamId?: string | null
  tier?: number | null
  customerId?: string
  tenantId?: string | null
  isSuperAdmin?: boolean
}

export interface UsersQuery {
  kind?: UserKind
  enabled?: 'true' | 'false'
  q?: string
}

export interface RoleScope {
  productIds: string[]
  customerIds: string[]
}

export interface Role {
  id: string
  slug: string
  displayName: string
  kind: UserKind
  fullAccess: boolean
  customerWide: boolean
  isSystem: boolean
  /** The effective set (every key for a full-access role). */
  permissions: string[]
  scopes: RoleScope[]
  _count: { users: number }
}

export interface RoleInput {
  slug?: string
  displayName?: string
  kind?: UserKind
  permissions?: string[]
  customerWide?: boolean
  scopes?: RoleScope[]
}

export interface PermissionGroup {
  group: string
  permissions: { key: string; label: string; customerGrantable: boolean }[]
}

export interface Team {
  id: string
  name: string
  createdAt: string
  _count: { users: number; tickets: number }
}

const json = (method: string, body: unknown) => ({ method, body: JSON.stringify(body) })

function usersPath(q: UsersQuery): string {
  const params = new URLSearchParams()
  if (q.kind) params.set('kind', q.kind)
  if (q.enabled) params.set('enabled', q.enabled)
  if (q.q) params.set('q', q.q)
  const qs = params.toString()
  return `/admin/users${qs ? `?${qs}` : ''}`
}

export const accessApi = {
  users: (q: UsersQuery): Promise<AdminUser[]> => request<AdminUser[]>(usersPath(q)),
  createUser: (input: UserInput): Promise<AdminUser & { invite: InviteResult }> =>
    request('/admin/users', json('POST', input)),
  updateUser: ({ id, ...input }: UserInput & { id: string }): Promise<AdminUser> =>
    request(`/admin/users/${id}`, json('PATCH', input)),
  setUserEnabled: ({ id, enabled }: { id: string; enabled: boolean }): Promise<AdminUser> =>
    request(`/admin/users/${id}/${enabled ? 'enable' : 'disable'}`, { method: 'POST' }),
  resendInvite: (id: string): Promise<{ invite: InviteResult }> =>
    request(`/admin/users/${id}/resend-invite`, { method: 'POST' }),

  permissions: (): Promise<PermissionGroup[]> => request<PermissionGroup[]>('/admin/permissions'),
  roles: (): Promise<Role[]> => request<Role[]>('/admin/roles'),
  createRole: (input: RoleInput): Promise<Role> => request('/admin/roles', json('POST', input)),
  updateRole: ({ id, ...input }: RoleInput & { id: string }): Promise<Role> =>
    request(`/admin/roles/${id}`, json('PATCH', input)),
  deleteRole: (id: string): Promise<void> => request(`/admin/roles/${id}`, { method: 'DELETE' }),

  teams: (): Promise<Team[]> => request<Team[]>('/admin/teams'),
  createTeam: (name: string): Promise<Team> => request('/admin/teams', json('POST', { name })),
  renameTeam: ({ id, name }: { id: string; name: string }): Promise<Team> =>
    request(`/admin/teams/${id}`, json('PATCH', { name })),
  deleteTeam: (id: string): Promise<void> => request(`/admin/teams/${id}`, { method: 'DELETE' }),
}
